<template>
    <div>

        <div class="mx-auto w-1/2 text-center">
            <h1 class="first-letter:uppercase text-heading-1 font-semibold mr-auto mb-16">{{ $t('import.step_4.title') }}</h1>
            <p class="text-primary-80 mb-32">{{ $t('import.step_4.description') }}</p>
        </div>

        <div class="grid grid-cols-4 gap-32 text-center mb-20">
            <strong class="col-start-2 col-span-1 text-body">{{ $t('import.step_4.table_headers') }}</strong>
            <strong class="col-start-3 col-span-1 text-body">{{ $t('import.step_4.table_fields') }}</strong>
        </div>
        <div v-for="field in filteredFields" class="flex items-center justify-center mb-16">
            <div class="w-320">
                <div class="border border-primary-5 rounded py-8">
                    <span class="px-12 min-h-24 flex items-center">
                        {{ field }}
                        <!--                        <span class="ml-auto text-body-sm bg-primary-5 text-primary-60 rounded-32 py-3 px-8 mr-8">-->
                        <!--                            Required-->
                        <!--                        </span>-->
                    </span>
                </div>
            </div>

            <div class="relative flex items-center w-48">
                <div :class="transformFields[field] ? 'bg-primary-80':'bg-primary-20'"
                     class="absolute -left-8  rounded-32 h-16 w-16 border-2 border-white shadow-sm trans-ease z-10"></div>
                <div :class="transformFields[field] ? 'h-1 border-t-1 border-primary-80 border':'h-2 border-t-2 border-primary-20 border-dashed'"
                     class="absolute inset-x-0 trans-ease z-0"></div>
                <div :class="transformFields[field] ? 'bg-primary-80':'bg-primary-20'"
                     class="absolute -right-8 rounded-32 h-16 w-16 border-2 border-white shadow-sm trans-ease z-10"></div>
            </div>

            <div class="w-320">
                <select-dropdown :auto-translate="false"
                                 :options="jobtoolzFields"
                                 :value="transformFields[field]"
                                 :placeholder="$t('import.general.choose_field')"
                                 searchable
                                 @change="$emit('setField', { key: field, value: $event })"/>
            </div>
        </div>


        <div class="mx-auto w-1/2 text-center mt-32">
            <p v-html="$t('import.step_4.unmatched_columns', { count: unmatchedFieldCount})"></p>


            <div v-if="requiredFieldsLeft" class="mt-16">
                <p><span>{{ requiredFieldsLeft }}</span> {{ $t('import.step_4.required_fields') }}</p>

                <p>{{ $t('import.step_4.map_fields') }}</p>

                <span v-for="requiredField in requiredFields" class="ml-12 text-body-sm rounded-32 py-3 px-8"
                      :class="requiredField.completed ? 'bg-primary-5 text-primary-40 line-through' : 'bg-primary-5 text-primary-60'">
                    {{ requiredField.label }}
                </span>
            </div>
        </div>


        <div class="mt-48 flex justify-center space-x-20">
            <simple-button class="uppercase w-200"
                           look="secondary"
                           @click="$emit('previous')">
                {{ $t('general.back') }}
            </simple-button>

            <simple-button v-if="data && data.length && !requiredFieldsLeft"
                           class="uppercase w-200"
                           display="secondary"
                           look="primary"
                           @click="$emit('next')">
                {{ $t('jobs.next_step') }}
            </simple-button>
        </div>
    </div>
</template>

<script>
import SimpleButton from '../SimpleButton';
import SelectDropdown from '../../forms/SelectDropdown';

export default {
    components: {SelectDropdown, SimpleButton},
    props: {
        file: File,
        data: Array,
        fields: Array,
        job_id: [Number, String],
        job_column: String,
        phase_id: [Number, String],
        phase_column: String,
        fieldOptions: Array,
        transformFields: Object,
    },
    computed: {
        filteredFields() {
            return this.fields.filter(i => ![this.job_column, this.phase_column].includes(i));
        },
        unmatchedFieldCount() {
            return this.filteredFields.length - Object.keys(this.transformFields).length;
        },
        requiredFields() {
            return this.jobtoolzFields.filter(field => ['first_name', 'last_name', 'full_name', 'email'].includes(field.value)).map(field => {
                const selectedFields = Object.values(this.transformFields);

                if (selectedFields.includes(field.value)) {
                    field.completed = true;
                }

                if (['first_name', 'last_name', 'full_name'].includes(field.value) &&
                    ((selectedFields.includes('first_name') && selectedFields.includes('last_name')) || selectedFields.includes('full_name'))) {
                    field.completed = true;
                }

                return field;
            });
        },
        requiredFieldsLeft() {
            return this.requiredFields.filter(i => !i.completed).length;
        },
        jobtoolzFields() {
            return [
                {
                    label: 'Don\'t copy this field.',
                    value: null,
                },
                {
                    label: 'Created time',
                    value: 'created_at',
                },
                {
                    label: 'Last updated time',
                    value: 'updated_at',
                },
                {
                    label: 'Full name',
                    value: 'full_name',
                },
                {
                    label: 'First name',
                    value: 'first_name',
                },
                {
                    label: 'Last name',
                    value: 'last_name',
                },
                {
                    label: 'CV (Link to file)',
                    value: 'cv_link',
                },
                {
                    label: 'Document (Link to file)',
                    value: 'document_link',
                },
                {
                    label: 'Street name',
                    value: 'street',
                },
                {
                    label: 'House number',
                    value: 'house_number',
                },
                {
                    label: 'City',
                    value: 'city',
                },
                {
                    label: 'Postal code',
                    value: 'postal_code',
                },
                {
                    label: 'Country code',
                    value: 'country',
                },
                {
                    label: 'Email address',
                    value: 'email',
                },
                {
                    label: 'Phone number',
                    value: 'phone_number',
                },
                {
                    label: 'Birthdate',
                    value: 'birthdate',
                },
                {
                    label: 'Place of birth',
                    value: 'birthplace',
                },
                {
                    label: 'Salary expectation',
                    value: 'salary_expectation',
                },
                {
                    label: 'Motivation',
                    value: 'relevant_info',
                },
                {
                    label: 'Linked in account',
                    value: 'linkedin',
                },
                {
                    label: 'Facebook',
                    value: 'facebook',
                },
                {
                    label: 'Twitter',
                    value: 'twitter',
                },
                {
                    label: 'Language of candidate',
                    value: 'locale',
                },
                {
                    label: 'Instagram',
                    value: 'instagram',
                },
                {
                    label: 'Nationality',
                    value: 'nationality',
                },
                {
                    label: 'National Insurance Number',
                    value: 'rijksregister_nr',
                },
                {
                    label: 'IBAN',
                    value: 'iban_nr',
                },
                {
                    label: 'Rejected (true or false)',
                    value: 'rejected',
                },
                {
                    label: 'Rejected reason',
                    value: 'reject_reason',
                },
                {
                    label: 'Hired time (if applicable)',
                    value: 'completed_at',
                },
                {
                    label: 'Referrer URL',
                    value: 'referrer',
                },
                {
                    label: 'Referrer name',
                    value: 'referrer_source',
                },
                {
                    label: 'Extra tag',
                    value: 'tag',
                },
                {
                    label: 'Extra comment',
                    value: 'comment',
                },
                {
                    label: 'Extra comment (with column title)',
                    value: 'comment_with_key',
                },
                // Photo & CV
                /*{
                    label: '',
                    value: ''
                },
                {
                    label: '',
                    value: ''
                },*/

            ];
        },
    },
};
</script>
